import { ProgressBar as HeroProgress } from '@heroui/react/progress-bar';
import { cn } from '@/utils/cn';
import type { ReactNode } from 'react';

/**
 * Determinate progress bar — file uploads, multi-step work, and any ratio
 * shown against a goal.
 *
 * Built on HeroUI's ProgressBar so the `role="progressbar"` semantics and the
 * `aria-valuenow`/`aria-valuetext` wiring come from react-aria rather than
 * being hand-rolled. Pass `label` so assistive tech announces WHAT is
 * progressing, not just a bare percentage.
 */
export interface ProgressProps {
  /** 0–100. Values outside the range are clamped. */
  value: number;
  label?: string;
  /**
   * Name the bar for assistive tech without printing it — for a bar whose
   * label is already written beside it.
   */
  hideLabel?: boolean;
  /** Render the numeric percentage beside the label. */
  showValue?: boolean;
  /**
   * `warning` is the middle step of a three-band benchmark (healthy / watch /
   * needs attention). `neutral` is for a bar carrying no judgement at all.
   */
  tone?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
  size?: 'sm' | 'md';
  /**
   * Draws a marker at this percentage — the goal the bar is read against.
   *
   * Without it a benchmark has to live in prose underneath ("aim for 95%"),
   * which gives the eye no sense of where 95% falls on a track scaled 0–100.
   */
  target?: number;
  /** Tooltip for the target marker. Required whenever `target` is set. */
  targetLabel?: string;
  /**
   * Replaces the announced value. Use it to state the value *and* its target
   * together, so a screen reader hears the comparison a sighted reader gets
   * from the marker.
   */
  valueLabel?: ReactNode;
  className?: string;
}

const COLOR = {
  primary: 'accent',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  neutral: 'default',
} as const;

export function Progress({
  value,
  label,
  hideLabel = false,
  showValue = false,
  tone = 'primary',
  size = 'sm',
  target,
  targetLabel,
  valueLabel,
  className,
}: ProgressProps) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));

  return (
    <HeroProgress.Root
      value={pct}
      aria-label={label ?? 'Progress'}
      color={COLOR[tone]}
      size={size}
      valueLabel={valueLabel}
      className={cn('w-full', className)}
    >
      {((label && !hideLabel) || showValue) && (
        <div className="mb-1 flex items-center justify-between gap-2">
          {label && !hideLabel && (
            <span className="text-small text-muted">{label}</span>
          )}
          {showValue && (
            <HeroProgress.Output className="text-small tabular-nums text-muted">
              {pct}%
            </HeroProgress.Output>
          )}
        </div>
      )}
      <HeroProgress.Track
        className={target !== undefined ? 'relative' : undefined}
      >
        <HeroProgress.Fill />
        {target !== undefined && (
          // `insetInlineStart` rather than `left`: the fill grows from the
          // inline start, so under RTL the marker has to travel with it.
          // `aria-hidden` because the target is already spoken through
          // `valueLabel` — announcing it twice is noise.
          <span
            className="absolute top-1/2 h-3 w-0.5 -translate-y-1/2 rounded-full bg-foreground/50"
            style={{
              insetInlineStart: `${Math.max(0, Math.min(100, target))}%`,
            }}
            title={targetLabel}
            aria-hidden
          />
        )}
      </HeroProgress.Track>
    </HeroProgress.Root>
  );
}
